import { Directive, HostListener, Optional } from '@angular/core';
import { NgControl } from '@angular/forms';

const SKIP_TYPES = ['password', 'file', 'checkbox', 'radio', 'range', 'color', 'hidden'];

@Directive({
  selector: 'input[formControlName], input[formControl], input[ngModel], textarea[formControlName], textarea[formControl], textarea[ngModel]',
  standalone: true,
})
export class TrimWhitespaceDirective {

  constructor(@Optional() private ngControl: NgControl) {}

  /** Real-time: block leading spaces only on non-email fields while typing */
  @HostListener('input', ['$event'])
  onInput(event: Event): void {
    const target = event.target as HTMLInputElement | HTMLTextAreaElement | null;
    if (!target) return;
    if (target instanceof HTMLInputElement && SKIP_TYPES.includes(target.type?.toLowerCase())) return;
    // Skip email inputs entirely during typing — cursor bugs with type switching.
    // Spaces in email are removed on blur and on form submit.
    if (target instanceof HTMLInputElement && target.type.toLowerCase() === 'email') return;

    const raw = target.value ?? '';
    const selStart = (target as HTMLInputElement).selectionStart ?? raw.length;
    const cleaned = raw.replace(/^\s+/, '');
    if (raw === cleaned) return;
    const leadingSpaces = raw.length - raw.trimStart().length;
    const newCursor = Math.max(0, selStart - leadingSpaces);
    target.value = cleaned;
    try { (target as HTMLInputElement).setSelectionRange(newCursor, newCursor); } catch { }
    this.ngControl?.control?.setValue(cleaned, { emitEvent: true, emitModelToViewChange: false, emitViewToModelChange: false });
  }

  /** On blur: trim ends for all fields; strip ALL spaces for email fields */
  @HostListener('blur', ['$event'])
  onBlur(event: Event): void {
    const target = event.target as HTMLInputElement | HTMLTextAreaElement | null;
    if (!target) return;
    if (target instanceof HTMLInputElement && SKIP_TYPES.includes(target.type?.toLowerCase())) return;
    const isEmail = target instanceof HTMLInputElement && target.type.toLowerCase() === 'email';
    const raw = this.readRaw(target);
    const cleaned = isEmail ? raw.replace(/\s/g, '') : raw.trim();
    if (raw !== cleaned) {
      target.value = cleaned;
      this.ngControl?.control?.setValue(cleaned);
    }
  }

  private readRaw(target: HTMLInputElement | HTMLTextAreaElement): string {
    if (target instanceof HTMLInputElement && target.type.toLowerCase() === 'email') {
      target.type = 'text';
      const raw = target.value ?? '';
      target.type = 'email';
      return raw;
    }
    return target.value ?? '';
  }
}
